import type { FC } from 'react'
import { styled } from '@mui/material/styles'
import useTranslate from '@/hooks/use-translate'

interface LoadingLogoProps {
  width?: number
  height?: number
}

const CYCLE = '0.9s'

const LogoSvg = styled('svg')`
  display: block;
  color: ${({ theme }) => theme.palette.primary.main};
  overflow: visible;
  animation: cycleFade ${CYCLE} ease-in-out infinite;

  .bar {
    fill: none;
    stroke: currentColor;
    stroke-width: 9;
    stroke-linecap: round;
    stroke-dasharray: 100;
    stroke-dashoffset: 100;
    opacity: 0.5;
  }

  .bar-vertical {
    animation: drawVertical ${CYCLE} ease-in-out infinite;
  }

  .bar-diagonal {
    animation: drawDiagonal ${CYCLE} ease-in-out infinite;
  }

  .dot {
    fill: currentColor;
    transform-box: fill-box;
    transform-origin: center;
    opacity: 0;
    animation: dotIn ${CYCLE} ease-in-out infinite;
  }

  @keyframes cycleFade {
    0%,
    67% {
      opacity: 1;
    }
    87.5%,
    100% {
      opacity: 0;
    }
  }

  @keyframes drawVertical {
    0% {
      stroke-dashoffset: 100;
    }
    29%,
    87% {
      stroke-dashoffset: 0;
    }
    88%,
    100% {
      stroke-dashoffset: 100;
    }
  }

  @keyframes drawDiagonal {
    0%,
    6% {
      stroke-dashoffset: 100;
    }
    35%,
    87% {
      stroke-dashoffset: 0;
    }
    88%,
    100% {
      stroke-dashoffset: 100;
    }
  }

  @keyframes dotIn {
    0%,
    29% {
      opacity: 0;
      transform: scale(0);
    }
    46%,
    87% {
      opacity: 1;
      transform: scale(1);
    }
    88%,
    100% {
      opacity: 0;
      transform: scale(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    .bar,
    .dot {
      animation: none;
    }

    .bar {
      stroke-dashoffset: 0;
    }

    .dot {
      opacity: 1;
      transform: none;
    }
  }
`

export const LoadingLogo: FC<LoadingLogoProps> = ({
  width = 38,
  height = 38,
}) => {
  const { t } = useTranslate('common')
  const label = t('nabuLogo')

  return (
    <LogoSvg
      width={width}
      height={height}
      viewBox="0 0 27 27"
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      <path className="bar bar-vertical" d="M4.5 4.5 L4.5 22.5" pathLength="100" />
      <path
        className="bar bar-diagonal"
        d="M4.5 4.5 L22.5 22.5"
        pathLength="100"
      />
      <circle className="dot" cx="4.5" cy="4.5" r="4.5" />
      <circle className="dot" cx="4.5" cy="22.5" r="4.5" />
      <circle className="dot" cx="22.5" cy="22.5" r="4.5" />
      <circle className="dot" cx="22.5" cy="4.5" r="4.5" />
    </LogoSvg>
  )
}
